import React, { Component } from 'react'
import styled, { css } from 'styled-components'
import { breakpoints, media, style } from 'app/assets/styles'
import { Layout } from 'app/common/layout'
import { Link } from 'app/common/link'
import { FooterNotice } from 'app/common/footer-notice'
import { Hero } from 'app/common/hero'
import { Cards } from 'app/common/cards'
import Responsive from 'react-responsive'
import { FeaturedProducts } from 'app/common/featured-products'
import { ErrorManagement } from 'app/common/error-management'
import { H4 } from 'app/common/typography'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import type {
    MarketingAnimationsData,
    NearbyRestaurantsDTO
} from 'shared/types'
import { Placeholder } from 'app/common/placeholder'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import { colors } from 'app-v2/design-systems/theme'
import { BagOnClick } from 'app/assets/icons'
import type { FetchOrdersParams } from 'shared/store/ecommerce/order'
import { MarketingAnimationKingdom } from './MarketingAnimationKingdom'
import { IconButton } from 'app-v2/design-systems/components/inputs/button'
import { FeatureToggle } from 'app/common/feature-toggle'
import { featureCodes } from 'shared/utils'

const { medium } = breakpoints

type Props = {
    fetchPageData: () => void,
    fetchOrders: (fetchOrdersParams?: FetchOrdersParams) => void,
    marketingAnimations: MarketingAnimationsData,
    nearbyRestaurants: NearbyRestaurantsDTO,
    error: any,
    networkError: any,
    authenticated: ?boolean,
    intl: IntlShape,
    displayableOrdersNumber: number,
    tableOrderEnabled: boolean
}

type State = {
    width: number
}

class Home extends Component<Props, State> {
    constructor() {
        super()
        this.state = {
            width: window.innerWidth
        }
    }

    componentDidMount() {
        const { fetchOrders, fetchPageData } = this.props
        fetchPageData()
        fetchOrders({ days: 1 })
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    render() {
        const {
            nearbyRestaurants,
            intl,
            marketingAnimations,
            error,
            networkError,
            authenticated,
            displayableOrdersNumber,
            tableOrderEnabled
        } = this.props
        const {
            primary,
            secondary,
            suggestions,
            footerNotice
        } = marketingAnimations
        const { width } = this.state
        const mobile = width < medium
        const title = 'home.section.cards.title'
        const subTitle = 'home.section.cards.subtitle'
        const hasOrders = displayableOrdersNumber > 0

        return (
            <ErrorManagement error={error} networkError={networkError}>
                <Layout background="gradient" color="white">
                    <FeatureToggle feature={featureCodes.CLICK_AND_COLLECT}>
                        <Responsive maxWidth={breakpoints.medium}>
                            <StyledOrdersContainer>
                                {hasOrders && (
                                    <StyledLink
                                        background={colors.fieryRed}
                                        url={intl.formatMessage({
                                            id: 'routes.kingdom.orders.full'
                                        })}>
                                        <H4>
                                            <FormattedMessage
                                                id="home.orders.button"
                                                values={{
                                                    ordersNumber: displayableOrdersNumber
                                                }}
                                            />
                                        </H4>
                                    </StyledLink>
                                )}
                                {!tableOrderEnabled && !hasOrders && (
                                    <CcCartCheck>
                                        <IconButton
                                            icon={<BagOnClick size={15} />}
                                            fullLength>
                                            <FormattedMessage id="clickandcollect" />
                                        </IconButton>
                                    </CcCartCheck>
                                )}
                            </StyledOrdersContainer>
                        </Responsive>
                    </FeatureToggle>
                    {primary && primary.length > 0 && (
                        <Placeholder
                            minHeightMobile="140vw"
                            minHeightDesktop="40vw">
                            <Hero primary={primary} />
                        </Placeholder>
                    )}
                    {secondary && secondary.length > 0 && (
                        <Cards
                            padding={mobile ? '0 0 30px' : '0 0 40px'}
                            secondary={secondary}
                            title={title}
                            subTitle={subTitle}
                        />
                    )}
                    {!authenticated && (
                        <MarketingAnimationKingdom
                            kingdom={marketingAnimations?.kingdom}
                            mobile={mobile}
                        />
                    )}
                    {suggestions && suggestions.products && (
                        <FeaturedProducts
                            title={intl.formatMessage({
                                id: 'home.products.strate.title'
                            })}
                            suggestions={suggestions}
                        />
                    )}
                    {footerNotice && footerNotice.actionLabel && (
                        <FooterNotice {...footerNotice} />
                    )}
                </Layout>
            </ErrorManagement>
        )
    }
}

const StyledMentionCards = styled.div`
    width: 100%;
    ${props =>
        props.layout &&
        media.medium`
        width: 38%;
    `};
`

const StyledOrdersContainer = styled.div`
    position: absolute;
    top: 75px;
    left: 5%;
    right: 5%;
    z-index: 999;
    width: 90%;
`

const buttonStyle = css`
    width: 100%;
    align-self: center;
    color: white;
    border: none;
    padding: 5px 0;
`

const StyledLink = styled(Link)`
    ${style.buttonSmall};
    ${buttonStyle}
`

const HomeComponent = injectIntl(Home)

export { HomeComponent }
